import type {BlockArgs, CategoryPrototype} from './category_prototype';
import type Runtime from '../engine/runtime';
import type BlockUtility from '../engine/block-utility';
import type RenderedTarget from '../sprites/rendered-target';

interface StatementArgumentValue {
    entry?: string | null;
    callerId?: string | null;
    callerTarget?: RenderedTarget | null;
}

class Scratch3ProcedureBlocks implements CategoryPrototype {
    constructor (
        /**
         * The runtime instantiating this block package.
         */
        public runtime: Runtime
    ) {
    }

    /**
     * Retrieve the block primitives implemented by this package.
     * @returns Mapping of opcode to Function.
     */
    getPrimitives () {
        return {
            procedures_definition: this.noOp,
            procedures_call: this.call,
            procedures_return: this.return,
            procedures_discard: this.noOp,
            argument_reporter_string_number: this.argumentReporterStringNumber,
            argument_reporter_boolean: this.argumentReporterBoolean,
            argument_reporter_statement: this.argumentReporterStatement
        };
    }

    noOp () {
        // No-op: execute the blocks.
    }

    call (args: BlockArgs, util: BlockUtility) {
        const procedureCode = args.mutation!.proccode;
        const paramNamesIdsAndDefaults = util.getProcedureParamNamesIdsAndDefaults(procedureCode);

        // If null, procedure could not be found, which can happen if custom
        // block is dragged between sprites without the definition.
        // Match Scratch 2.0 behavior and noop.
        if (paramNamesIdsAndDefaults === null) {
            return;
        }

        const [paramNames, paramIds, paramDefaults] = paramNamesIdsAndDefaults;
        const callerFrame = util.thread!.peekStackFrame?.();
        const callerTarget = callerFrame?.target ?? util.thread!.target;

        // Initialize params for the current stackFrame to {}, even if the procedure does
        // not take any arguments. This is so that `getParam` down the line does not look
        // at earlier stack frames for the values of a given parameter (#1729)
        util.initParams();
        for (let i = 0; i < paramIds.length; i++) {
            if (Object.prototype.hasOwnProperty.call(args, paramIds[i])) {
                util.pushParam(paramNames[i], args[paramIds[i]]);
            } else if (paramIds[i].startsWith('SUBSTACK')) {
                // Pass the caller's statement input to the callback reporter.
                const callback: StatementArgumentValue = {
                    entry: paramIds[i],
                    callerId: util.currentBlockId
                };
                if (callerTarget) {
                    callback.callerTarget = callerTarget;
                }
                util.pushParam(paramNames[i], callback);
            } else {
                util.pushParam(paramNames[i], paramDefaults[i]);
            }
        }

        util.stackFrame.executed = true;
        util.startProcedure(procedureCode);
    }

    return (args: BlockArgs, util: BlockUtility) {
        util.thread!.pushReportedValue(args.VALUE);
        util.stopThisScript();
    }

    argumentReporterStringNumber (args: BlockArgs, util: BlockUtility) {
        const value = util.getParam(args.VALUE);
        if (value === null) {
            // When the parameter is not found in the most recent procedure
            // call, the default is always 0.
            return 0;
        }
        return value;
    }

    argumentReporterBoolean (args: BlockArgs, util: BlockUtility) {
        const value = util.getParam(args.VALUE);
        if (value === null) {
            // When the parameter is not found in the most recent procedure
            // call, the default is always 0.
            return 0;
        }
        return value;
    }

    argumentReporterStatement (args: BlockArgs, util: BlockUtility) {
        const branchInfo = util.getParam(args.VALUE) as StatementArgumentValue | null;
        if (!branchInfo?.entry || !branchInfo.callerId) {
            util.thread!.pushStack(null);
            return;
        }

        const branch = util.getBranchAndTarget(
            branchInfo.callerId,
            branchInfo.entry,
            branchInfo.callerTarget
        );
        if (branch) {
            util.thread!.pushStack(branch[0], branch[1]);
        } else {
            util.thread!.pushStack(null);
        }
    }
}

export default Scratch3ProcedureBlocks;
